import { makeStyles } from "@mui/styles";
import { opacity2hex } from "utils/miscUtils";

const useStyles = makeStyles((theme) => ({
  // Main container with improved spacing and responsive design
  mainContainer: {
    padding: "24px",
    backgroundColor: "#f8f9fa",
    minHeight: "100vh",
    [theme.breakpoints.down("md")]: {
      padding: "16px",
    },
    [theme.breakpoints.down("sm")]: {
      padding: "12px",
    },
  },

  // Section card styling for better organization
  sectionCard: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    padding: "24px",
    marginBottom: "24px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    border: "1px solid #e0e4e7",
    [theme.breakpoints.down("md")]: {
      padding: "20px",
      marginBottom: "20px",
    },
    [theme.breakpoints.down("sm")]: {
      padding: "16px",
      marginBottom: "16px",
    },
  },

  // Section title styling
  sectionTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: theme.palette.primary.main,
    marginBottom: "8px",
  },

  // Section divider
  sectionDivider: {
    marginBottom: "20px",
    backgroundColor: "#e0e4e7",
  },

  // Responsive form grid
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "20px",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
      gap: "16px",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      gap: "12px",
    },
  },

  // Form field styling
  formField: {
    minWidth: "0", // Prevents grid overflow
  },

  // Wide form field for multi-select inputs
  formFieldWide: {
    gridColumn: "1 / -1",
    [theme.breakpoints.down("sm")]: {
      gridColumn: "auto",
    },
  },

  // Image section styling
  imageSection: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    [theme.breakpoints.down("sm")]: {
      gap: "16px",
    },
  },

  // Image picker container
  imagePickerContainer: {
    display: "flex",
    alignItems: "flex-start",
    gap: "24px",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      gap: "16px",
    },
  },

  // Image preview container
  imagePreviewContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
  },

  // Image preview label
  imagePreviewLabel: {
    fontSize: "14px",
    fontWeight: "500",
    color: theme.palette.text.secondary,
  },

  // Image preview
  imagePreview: {
    maxWidth: "200px",
    maxHeight: "200px",
    objectFit: "contain",
    borderRadius: "8px",
    border: "1px solid #e0e4e7",
  },

  // Subsection container
  subsectionContainer: {
    marginBottom: "20px",
    "&:last-child": {
      marginBottom: 0,
    },
  },

  // Subsection title
  subsectionTitle: {
    fontSize: "16px",
    fontWeight: "500",
    color: theme.palette.text.primary,
    marginBottom: "16px",
  },

  // Action buttons container
  actionButtonsContainer: {
    display: "flex",
    gap: "12px",
    marginBottom: "16px",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },

  // Operator price row
  operatorPriceRow: {
    display: "flex",
    alignItems: "flex-end",
    gap: "16px",
    marginBottom: "16px",
    padding: "16px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    border: "1px solid #e0e4e7",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      alignItems: "stretch",
      gap: "12px",
    },
  },

  // Operator pack row
  operatorPackRow: {
    display: "flex",
    alignItems: "flex-end",
    gap: "16px",
    marginBottom: "16px",
    padding: "16px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    border: "1px solid #e0e4e7",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      alignItems: "stretch",
      gap: "12px",
    },
  },

  // Delete button container
  deleteButtonContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("md")]: {
      alignSelf: "flex-end",
    },
  },

  // Delete button styling
  deleteButton: {
    color: theme.palette.error.main,
    "&:hover": {
      backgroundColor: "rgba(244, 67, 54, 0.04)",
    },
  },

  // Slot header container
  slotHeaderContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "12px",
    },
  },

  // Slot count container
  slotCountContainer: {
    minWidth: "150px",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },

  // Slots container
  slotsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },

  // Individual slot card
  slotCard: {
    backgroundColor: "#f8f9fa",
    borderRadius: "12px",
    padding: "20px",
    border: "1px solid #e0e4e7",
    [theme.breakpoints.down("sm")]: {
      padding: "16px",
    },
  },

  // Slot header
  slotHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "16px",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      gap: "12px",
    },
  },

  // Slot header left section
  slotHeaderLeft: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    flex: 1,
  },

  // Slot header right section
  slotHeaderRight: {
    display: "flex",
    alignItems: "center",
  },

  // Slot title
  slotTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: theme.palette.primary.main,
  },

  // Slot type container
  slotTypeContainer: {
    maxWidth: "200px",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
    },
  },

  // Player configuration section
  playerConfigSection: {
    marginBottom: "16px",
  },

  // Player configuration row
  playerConfigRow: {
    display: "flex",
    gap: "20px",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      gap: "16px",
    },
  },

  // Player tags section
  playerTagsSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },

  // Tag input container
  tagInputContainer: {
    minWidth: "250px",
    [theme.breakpoints.down("sm")]: {
      minWidth: "100%",
    },
  },

  // Tags display container
  tagsDisplayContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    padding: "8px 0",
  },

  // Slot content
  slotContent: {
    marginTop: "16px",
  },

  // Button container for actions
  buttonContainer: {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column-reverse",
    },
  },

  // Legacy styles (kept for backward compatibility but updated)
  slotRoot: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    padding: "20px",
    marginTop: "20px",
  },

  addRemoveContainer: {
    display: "flex",
    flexFlow: "row wrap",
    gap: "8px",
    padding: "8px",
  },

  dropProbabilities: {
    marginLeft: "8px",
    display: "flex",
    flexFlow: "row wrap",
    gap: "8px",
  },

  slotSubHeaderContainer: {
    display: "flex",
    flexFlow: "row wrap",
    justifyContent: "space-between",
    marginBottom: "12px",
  },

  slotSubHeader: {
    display: "flex",
    flexFlow: "row wrap",
    justifyContent: "space-between",
  },

  container: {
    padding: "20px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
    },
  },

  cardContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "20px",
    justifyContent: "flex-start",
    marginTop: "33px",
    [theme.breakpoints.down("sm")]: {
      marginBottom: "10px",
      justifyContent: "center",
    },
  },

  formContainer: {
    display: "flex",
    flexFlow: "row wrap",
    gap: "10px",
  },

  PackFormContainer: {
    display: "flex",
    flexFlow: "column wrap",
  },

  noPlayersContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 150,
    backgroundColor: "#D3D3D3",
    borderRadius: 12,
    width: "100%",
    marginTop: "33px",
    [theme.breakpoints.down("sm")]: {
      marginBottom: "10px",
    },
  },

  smallField: {
    width: "150px",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },

  field: {
    width: "220px",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },

  field2: {
    width: "140px",
    margin: "0 8px 5px 8px",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      margin: "0 0 5px 0",
    },
  },

  field3: {
    width: "250px",
    margin: "0 8px 5px 8px",
    marginTop: 15,
    marginBottom: 10,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      margin: "0 0 10px 0",
    },
  },

  field5: {
    marginTop: 15,
    width: "400px",
    margin: "0 8px 5px 8px",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      margin: "15px 0 5px 0",
    },
  },

  field4: {
    width: "180px",
    margin: "0 8px 5px 8px",
    marginTop: 15,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      margin: "15px 0 5px 0",
    },
  },

  button: {
    width: "150px",
    height: 45,
  },

  submitButton: {
    marginTop: 20,
    width: "150px",
    height: 45,
  },

  savecancel: {
    marginTop: 20,
    width: "100px",
    height: 40,
  },

  cancelb: {
    marginTop: 20,
    width: "100px",
    height: 40,
    backgroundcolor: "red",
  },

  removeButton: {
    position: "absolute",
    top: "-14px",
    right: "-7px",
    padding: 0,
    background: theme.palette.secondary.main,
    color: "#fff",
    "&:hover": { color: "#000" },
  },

  playerCardWrapper: {
    width: "fit-content",
    position: "relative",
  },

  slotConfigContainer: {
    display: "flex",
    flexFlow: "column wrap",
    marginTop: 10,
    gap: 10,
    width: "100%",
  },

  slotConfigHeader: {
    fontSize: 14,
    fontWeight: "bold",
    marginLeft: 6,
  },

  slotContainer: {
    padding: 12,
    marginTop: 15,
    backgroundColor: `#D3D3D3${opacity2hex("0.35")}`,
    borderRadius: 12,
  },

  slotNumberText: {
    marginLeft: 8,
    fontSize: 12,
    fontWeight: "bold",
  },

  playerSlotContainer: {
    display: "flex",
    flexFlow: "row wrap",
    gap: 15,
  },

  boosterSlotContainer: {
    marginTop: 20,
    justifyContent: "flex-end",
    display: "flex",
    flexFlow: "row wrap",
    gap: 15,
  },

  rowProbabilities: {
    display: "flex",
    flexFlow: "column wrap",
  },

  errorMessage: {
    color: "red",
    fontSize: 15,
    fontFamily: ["Open Sans", "sans-serif"].join(","),
  },

  slotEditor: {
    marginTop: 10,
  },

  slotHeaderText: {
    marginTop: 20,
    fontWeight: 700,
  },

  prevContainer: {
    display: "flex",
    flexFlow: "column nowrap",
  },

  preview: {
    aspectRatio: "16/9",
    height: "220px",
    objectFit: "contain",
    marginTop: 2,
  },

  playerTagContainer: {
    position: "relative",
    display: "flex",
    flexDirection: "row",
    minWidth: 140,
    gap: 10,
    width: "calc(100% - 140px)",
    [theme.breakpoints.down("md")]: {
      width: "100%",
      minWidth: "100%",
    },
  },

  playerPosAndTagContainer: {
    display: "flex",
    gap: 1,
    margin: "0 0 5px 8px",
    position: "relative",
    width: "100%",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      gap: 12,
    },
  },

  chipContainer: {
    display: "flex",
    flexFlow: "row",
    gap: 2,
    overflow: "auto",
    width: "calc(100% - 240px)",
    margin: "26px auto auto 0px",
    [theme.breakpoints.down("md")]: {
      width: "100%",
      margin: "8px 0 0 0",
    },
  },

  "&::-webkit-scrollbar": {
    height: 4,
  },

  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#ccc",
    borderRadius: 4,
  },

  "&::-webkit-scrollbar-track": {
    backgroundColor: "#f1f1f1",
  },
}));

export default useStyles;
